/* eslint-disable react/no-unstable-nested-components */
import { useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useEngagementSubmittedSurveyData } from 'screens/Engagement/LandingPageV2/hooks/dataHooks';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { Loader } from 'screens/analytics/commonComponents';

import engagementNoDataImageV1 from 'assets/assetsV1/illustrations/newIllustrations/engagementNoDataImageV1.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey, getSubmittedPulseSurveyRoute } from 'constants/helper';
import { sendViewedEngagementSurvey } from 'constants/mixpanelHelper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const SubmittedSurveysIntentTable = ({ gridRef }) => {
  const history = useHistory();

  const { data, isLoading } = useEngagementSubmittedSurveyData({
    limit: 100,
    offset: 0,
    includeCount: true,
  });

  const { surveys: surveysList } = data ?? {};

  const handleViewDetails = (data) => {
    sendViewedEngagementSurvey({
      eventProperties: {
        surveyId: data?.data?.id,
      },
    });
    history.push(getSubmittedPulseSurveyRoute(data?.data?.id));
  };

  const columnDefs = useMemo(() => {
    const COLUMNS = [
      {
        headerName: 'Survey',
        field: 'name',
        cellRenderer: ({ value }) => (
          <Tooltip label={value} isDisabled={value.length < 40} width="100%">
            <Box width="100%">
              <TextBodyMedium textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
                {value}
              </TextBodyMedium>
            </Box>
          </Tooltip>
        ),
        width: 'auto',
        align: 'left',
        filter: 'agTextColumnFilter',
      },
      // {
      //   headerName: 'Drivers',
      //   field: 'surveyDrivers',
      //   cellRenderer: ({ value = [] }) => {
      //     const driversToShow = value.slice(0, 2);
      //     const remainingDriversCount = value.length > 2 ? `+${value.length - 2}` : '';

      //     return (
      //       <TextBodyMedium>
      //         {driversToShow.join(', ')} {remainingDriversCount}
      //       </TextBodyMedium>
      //     );
      //   },
      //   filter: 'agSetColumnFilter',
      // },
      {
        header: 'Submitted on',
        field: 'endDate',
        valueGetter: ({ data }) => data?.endDate?.value,
        filterValueGetter: ({ data }) => parseISO(data?.endDate?.value.split('T')[0]),
        cellRenderer: ({ value }) => {
          const parsedDate = value ? format(parseISO(value.split('T')[0]), 'dd MMM yyyy') : '—';
          return <TextBodyMedium>{parsedDate}</TextBodyMedium>;
        },
        filter: 'agDateColumnFilter',
      },
    ];
    return COLUMNS;
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      suppressHeaderMenuButton: true,
      headerComponent: CustomHeaderCell,
      minWidth: 200,
      flex: 1,
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
      headerComponentParams: {
        enableMenu: true,
        enableSorting: true,
      },
      menuTabs: ['filterMenuTab'],
    }),
    []
  );
  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.ENGAGEMENT;
    const viewId = 'My_Engagement';
    const tabId = 'SubmittedSurveys';
    const uniqueId = 'Engagement_Submitted_Surveys';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!surveysList?.length) {
    return (
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading="No surveys here!"
          description="You haven't responded to any surveys, when you do they will appear here."
          illustrationSrc={engagementNoDataImageV1}
        />
      </div>
    );
  }

  return (
    <AgGridTable
      defaultColDef={defaultColDef}
      gridStatePersistenceKey={gridStatePersistenceKey}
      ref={gridRef}
      rowData={surveysList}
      columnDefs={columnDefs}
      animateRows={false}
      domLayout="autoHeight"
      pagination
      headerHeight={36}
      rowHeight={52}
      autoHeight={false}
      sideBar={sideBar}
      onRowClicked={handleViewDetails}
      containerAutoHeight
    />
  );
};

export { SubmittedSurveysIntentTable };
